import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { getSubscriptionStatus, redeemInviteCode } from '@/services/subscriptionApi';
import type { SubscriptionStatus } from '@/types/subscription.types';
import { Link } from 'react-router-dom';

export function RedeemPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<SubscriptionStatus | null | 'loading'>('loading');
  const [redeemed, setRedeemed] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    getSubscriptionStatus()
      .then((data) => setStatus(data))
      .catch(() => setStatus(null));

    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const handleRedeem = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      addToast({
        title: '请输入邀请码',
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const res = await redeemInviteCode(trimmed);
      if (res.success) {
        setRedeemed(true);
        setStatus(res.data);
        addToast({
          title: '兑换成功',
          description: `已激活：${res.data.planName}`,
          variant: 'success',
        });
        redirectTimerRef.current = setTimeout(() => {
          navigate('/fsop');
        }, 3000);
      } else {
        addToast({
          title: '兑换失败',
          description: res.message || '请检查邀请码是否正确',
          variant: 'error',
        });
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || '网络错误，请稍后重试';
      addToast({
        title: '兑换失败',
        description: msg,
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isSubscribed = status !== null && status !== 'loading' && status.isValid;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Back link */}
        <div className="mb-4">
          <Link to="/settings">
            <Button variant="ghost" size="sm" className="text-gray-600">
              <ArrowLeft className="w-4 h-4 mr-1" />
              返回设置
            </Button>
          </Link>
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-3">
              <Gift className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-xl">兑换邀请码</CardTitle>
            <CardDescription>输入邀请码激活您的订阅</CardDescription>
          </CardHeader>

          <CardContent className="space-y-5">
            {/* Current status */}
            {status !== 'loading' && status !== null && (
              <div
                className={`flex items-center gap-3 p-3 rounded-lg text-sm ${
                  isSubscribed
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : status.status === 'expired'
                    ? 'bg-red-50 text-red-700 border border-red-200'
                    : 'bg-gray-50 text-gray-600 border border-gray-200'
                }`}
              >
                {isSubscribed ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                )}
                <div>
                  <span className="font-medium">
                    {isSubscribed
                      ? `当前订阅：${status.planName}`
                      : status.status === 'expired'
                      ? '订阅已过期'
                      : '未订阅'}
                  </span>
                  {status.expiresAt && (
                    <p className="text-xs opacity-80 mt-0.5">
                      {isSubscribed
                        ? `有效期至 ${formatDate(status.expiresAt)}`
                        : `已于 ${formatDate(status.expiresAt)} 过期`}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Success state */}
            {redeemed ? (
              <div className="text-center space-y-3 py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                <p className="text-lg font-semibold text-gray-900">兑换成功</p>
                <p className="text-sm text-gray-500">3 秒后自动跳转到首页...</p>
                <Button onClick={() => navigate('/fsop')} className="mt-2">
                  立即跳转
                </Button>
              </div>
            ) : (
              <>
                {/* Input */}
                <div className="space-y-2">
                  <label htmlFor="inviteCode" className="text-sm font-medium text-gray-700">
                    邀请码
                  </label>
                  <Input
                    id="inviteCode"
                    placeholder="请输入邀请码"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRedeem();
                    }}
                    disabled={isLoading}
                    className="text-center tracking-widest uppercase"
                    autoComplete="off"
                  />
                </div>

                {/* Redeem button */}
                <Button
                  onClick={handleRedeem}
                  disabled={isLoading || !code.trim()}
                  className="w-full"
                  size="lg"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      兑换中...
                    </>
                  ) : (
                    '立即兑换'
                  )}
                </Button>

                {/* Help text */}
                <p className="text-xs text-center text-gray-400">
                  没有邀请码？请联系管理员获取
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}
